import type { ExecuteWorkflowNodeRequest } from '@unifyapps/network/generated/models/executeWorkflowNodeRequest';
import type { ExecuteWorkflowNodeResponse } from '@unifyapps/network/generated/models/executeWorkflowNodeResponse';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { create } from 'mutative';
import { mutateDataWithLookupReferences } from './mutateDataWithLookupReferences';

const wildcard = `[*].`;

const adaptResponseRecursively = ({
  keys,
  data,
  lookupMap,
  name,
}: {
  name: string;
  keys: string;
  data: Record<string, unknown>;
  lookupMap: Record<string, unknown>;
}) => {
  const hasWildcard = keys.includes(wildcard);

  if (hasWildcard) {
    // path-> should return the remaining key after finding the first wildCard
    // example-> key: messages[*].message[*].data.citations[*].appName-> list:messages, path:message[*].data.citations[*].appName
    const [list, ...temp] = keys.split(wildcard);
    const path: string = temp.join(wildcard);

    if (!list || !path) {
      return;
    }
    const listData = get(data, list) as unknown[];

    if (!Array.isArray(listData)) {
      return;
    }

    if (path.includes(wildcard)) {
      return listData.forEach((item: Record<string, unknown>) => {
        adaptResponseRecursively({ keys: path, data: item, lookupMap, name });
      });
    }

    //leaf condition
    listData.forEach((item) => {
      mutateDataWithLookupReferences({
        data: item as Record<string, unknown>,
        path,
        name,
        lookupMap,
      });
    });
  } else {
    mutateDataWithLookupReferences({
      data,
      path: keys,
      name,
      lookupMap,
    });
  }
};

export const adaptResponseWithLookupReferences = (
  data?: ExecuteWorkflowNodeResponse,
  lookupRequests?: ExecuteWorkflowNodeRequest['lookupRequests'],
) => {
  if (!data?.response || !lookupRequests?.length || isEmpty(data.lookupReferences)) {
    return data?.response;
  }

  const lookupReferences = data.lookupReferences;

  return create(data.response, (draft) => {
    lookupRequests.forEach((lookupRequest) => {
      const name = lookupRequest.name;
      // @ts-expect-error -- should be present when lookupRequest is byKeys
      let keys = lookupRequest.keys as string | string[];

      if (typeof keys === 'string') {
        // backward compatibility for old lookupRequests - earlier it was a string
        keys = [keys];
      }

      const lookupMap = lookupReferences[name || ''] as Record<string, unknown> | undefined;

      if (!name || !keys.length || !lookupMap) {
        return;
      }

      keys.forEach((key) => {
        adaptResponseRecursively({ keys: key, data: draft, lookupMap, name });
      });
    });
  });
};
